import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "..\/..\/interfaces\/DecisionPriority";
import { Board, Move, IPiece, Position, PieceType } from "..\/..\/..\/..\/types";
import {
  MCTSUtils_v2,
  cloneFullGameState,
  getPieceFromPosition,
  applyMoveWithSnakeRespawn,
  validateMeadowEffects,
} from "..\/..\/..\/utils\/MCTSUtils";
import { MCTSMoveGenerationService } from "..\/..\/..\/utils\/MCTSMoveGenerationService";

/**
 * Priority 20: Two Turn Free Capture
 *
 * This priority SOLELY looks for UNSTOPPABLE 2-turn capture opportunities!
 * It does not look for general advancement or race moves!
 * It does not look for king safety or defensive moves!
 * It does not look for trade opportunities!
 * It does not look for piece specific heuristics!
 * It does not look for strategic advancement!
 * It does not look for back-up support!
 *
 * CRITICAL IMPLEMENTATION:
 * 1. PURE SIMULATION: No distance calculations - uses only simulation to determine feasibility
 * 2. 3-ply analysis: Our move → Enemy response → Our capture
 * 3. Target identification: Find enemy pieces that can be captured in 2 turns
 * 4. Verification: Ensure capture is possible regardless of ALL enemy responses
 * 5. Detailed logging: Shows targets, positions, and 3-ply attack plans
 *
 * It has one priority: if there is an unstoppable 2-turn capture opportunity, take it immediately!
 * This means after our move, regardless of enemy response, we can capture an enemy piece on our next turn.
 */
export class TwoTurnFreeCapture extends BaseDecisionPriority {
  readonly priority = 20;
  readonly name = "Two Turn Free Capture";
  readonly description =
    "CRITICAL: Execute ONLY genuine 2-turn capture opportunities (verified by 3-ply simulation) - NO general advancement";

  private analysisLogs: string[] = [];

  isRelevant(context: DecisionContext): boolean {
    const gameState = context.gameState;
    const isPlayerTurn = gameState.isLightTurn;

    // Find enemy pieces that could be potential targets
    const enemyPieces = this.findEnemyPieces(gameState, isPlayerTurn);
    if (enemyPieces.length === 0) return false;

    // Check if any of our moves can setup a 2-turn capture
    for (const ourMove of context.allPossibleMoves) {
      const ourPiece = MCTSUtils_v2.getPieceAt(
        gameState.board,
        ourMove.fromPosition
      );
      if (!ourPiece) continue;

      // Simulate our move
      const stateAfterOurMove = cloneFullGameState(gameState);
      const pieceToMove = getPieceFromPosition(
        stateAfterOurMove,
        ourMove.fromPosition
      );

      if (!pieceToMove) continue;

      try {
        // Apply our move
        applyMoveWithSnakeRespawn(
          stateAfterOurMove,
          ourMove.fromPosition,
          ourMove.move,
          pieceToMove
        );

        // Switch to enemy turn
        stateAfterOurMove.isLightTurn = !stateAfterOurMove.isLightTurn;

        // After enemy turn, check if we can capture any enemy piece
        const ourMovesAfterEnemyTurn =
          MCTSMoveGenerationService.getInstance().getAllMoves(
            stateAfterOurMove
          );

        // Look for capture opportunities in our follow-up moves
        for (const ourSecondMove of ourMovesAfterEnemyTurn) {
          const secondMovePiece = MCTSUtils_v2.getPieceAt(
            stateAfterOurMove.board,
            ourSecondMove.fromPosition
          );

          if (secondMovePiece && secondMovePiece.isLight === isPlayerTurn) {
            // Check if this move captures an enemy piece
            const targetPiece = MCTSUtils_v2.getPieceAt(
              stateAfterOurMove.board,
              ourSecondMove.move.position
            );

            if (targetPiece && targetPiece.isLight !== isPlayerTurn) {
              return true; // Found potential 2-turn capture
            }
          }
        }
      } catch (error) {
        continue; // Skip invalid moves
      }
    }

    return false; // No viable 2-turn capture opportunities found
  }

  analyze(context: DecisionContext): DecisionResult {
    // Initialize logging
    this.analysisLogs = [];

    let gameState: any;
    let isPlayerTurn: boolean;

    try {
      gameState = context.gameState;
      isPlayerTurn = gameState.isLightTurn;
    } catch (error) {
      this.addLog(`❌ Exception: ${error}`);
      return {
        shouldEarlyExit: false,
        reasoning: `Early exception: ${error}`,
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    // Find enemy pieces that could be potential targets
    const enemyPieces = this.findEnemyPieces(gameState, isPlayerTurn);
    this.addLog(
      `🎯 Found ${enemyPieces.length} enemy pieces as potential targets`
    );

    if (enemyPieces.length === 0) {
      this.addLog("❌ No enemy pieces found");
      return {
        shouldEarlyExit: false,
        reasoning: "No enemy pieces found",
        priority: this.priority,
        diagnostics: { logs: this.analysisLogs },
      };
    }

    // Check each of our moves for 2-turn capture setups
    const captureResult = this.checkMovesForCaptureOpportunities(
      context.allPossibleMoves,
      gameState,
      isPlayerTurn,
      enemyPieces
    );

    if (captureResult.shouldEarlyExit) {
      return captureResult;
    }

    this.addLog("❌ No unstoppable 2-turn capture opportunities found");
    return {
      shouldEarlyExit: false,
      reasoning: "No unstoppable 2-turn capture opportunities found",
      priority: this.priority,
      diagnostics: { logs: this.analysisLogs },
    };
  }

  private checkMovesForCaptureOpportunities(
    movesToAnalyze: MoveInfo[],
    gameState: any,
    isPlayerTurn: boolean,
    enemyPieces: Array<{ piece: IPiece; position: Position }>
  ): DecisionResult {
    for (const ourMove of movesToAnalyze) {
      const ourPiece = MCTSUtils_v2.getPieceAt(
        gameState.board,
        ourMove.fromPosition
      );
      if (!ourPiece) continue;

      // Simulate 3-ply for this move
      const simulationResult = this.simulate3PlyForCapture(
        ourMove,
        gameState,
        isPlayerTurn,
        enemyPieces
      );

      if (simulationResult.canCaptureInTwoTurns) {
        this.addLog(`✅ UNSTOPPABLE 2-TURN CAPTURE FOUND!`);
        this.addLog(`🎯 Setup Move: ${this.formatMove(ourMove)}`);
        this.addLog(`🎯 Target: ${simulationResult.targetDescription}`);
        this.addLog(`🎯 Attack Plan: ${simulationResult.attackPlan}`);

        return {
          shouldEarlyExit: true,
          selectedMove: ourMove,
          reasoning: `Unstoppable 2-turn capture: ${simulationResult.targetDescription} via ${simulationResult.attackPlan}`,
          priority: this.priority,
          diagnostics: {
            logs: this.analysisLogs,
            captureTarget: simulationResult.targetDescription,
            attackPlan: simulationResult.attackPlan,
          },
        };
      }
    }

    return {
      shouldEarlyExit: false,
      reasoning: "No unstoppable 2-turn captures found",
      priority: this.priority,
      diagnostics: { logs: this.analysisLogs },
    };
  }

  private simulate3PlyForCapture(
    ourMove: MoveInfo,
    gameState: any,
    isPlayerTurn: boolean,
    enemyPieces: Array<{ piece: IPiece; position: Position }>
  ): {
    canCaptureInTwoTurns: boolean;
    targetDescription: string;
    attackPlan: string;
  } {
    // PURE SIMULATION: No distance calculations - only simulation determines feasibility

    // Simulate our setup move
    const stateAfterOurMove = cloneFullGameState(gameState);
    const ourPiece = getPieceFromPosition(
      stateAfterOurMove,
      ourMove.fromPosition
    );

    if (!ourPiece) {
      return {
        canCaptureInTwoTurns: false,
        targetDescription: "Invalid piece for our move",
        attackPlan: "",
      };
    }

    // Apply our move using engine's standard move application
    try {
      applyMoveWithSnakeRespawn(
        stateAfterOurMove,
        ourMove.fromPosition,
        ourMove.move,
        ourPiece
      );
    } catch (error) {
      return {
        canCaptureInTwoTurns: false,
        targetDescription: `Move simulation failed: ${error}`,
        attackPlan: "",
      };
    }

    // Switch turn to enemy
    stateAfterOurMove.isLightTurn = !stateAfterOurMove.isLightTurn;

    // Generate all possible enemy responses
    const enemyMoves =
      MCTSMoveGenerationService.getInstance().getAllMoves(stateAfterOurMove);

    // For each enemy response, check if we can still capture something on our next turn
    for (const enemyMove of enemyMoves) {
      // Simulate enemy move
      const stateAfterEnemyMove = cloneFullGameState(stateAfterOurMove);
      const enemyPiece = getPieceFromPosition(
        stateAfterEnemyMove,
        enemyMove.fromPosition
      );

      if (!enemyPiece) continue;

      // CRITICAL CHECK: Make sure enemy isn't moving our pieces (engine bug workaround)
      if (enemyPiece.isLight === isPlayerTurn) continue;

      try {
        applyMoveWithSnakeRespawn(
          stateAfterEnemyMove,
          enemyMove.fromPosition,
          enemyMove.move,
          enemyPiece
        );
      } catch (error) {
        continue; // Skip invalid enemy moves
      }

      // Switch turn back to us
      stateAfterEnemyMove.isLightTurn = !stateAfterEnemyMove.isLightTurn;

      // Check if we can capture any enemy piece after this enemy response
      let canCaptureAfterThisEnemyMove = false;
      let captureTarget = "";
      let capturePlan = "";

      const ourMovesAfterEnemyResponse =
        MCTSMoveGenerationService.getInstance().getAllMoves(
          stateAfterEnemyMove
        );

      for (const ourFinalMove of ourMovesAfterEnemyResponse) {
        const finalMovePiece = MCTSUtils_v2.getPieceAt(
          stateAfterEnemyMove.board,
          ourFinalMove.fromPosition
        );

        if (finalMovePiece && finalMovePiece.isLight === isPlayerTurn) {
          // Check if this move captures an enemy piece
          const targetPiece = MCTSUtils_v2.getPieceAt(
            stateAfterEnemyMove.board,
            ourFinalMove.move.position
          );

          if (targetPiece && targetPiece.isLight !== isPlayerTurn) {
            canCaptureAfterThisEnemyMove = true;
            captureTarget = `${this.getPieceTypeString(
              targetPiece.type
            )} at ${this.formatChessPosition(ourFinalMove.move.position)}`;
            capturePlan = `${this.getPieceTypeString(
              finalMovePiece.type
            )} from ${this.formatChessPosition(
              ourFinalMove.fromPosition
            )} captures ${captureTarget}`;
            break;
          }
        }
      }

      // If we cannot capture after this enemy response, the capture is blockable
      if (!canCaptureAfterThisEnemyMove) {
        return {
          canCaptureInTwoTurns: false,
          targetDescription: "Enemy can block capture",
          attackPlan: "",
        };
      }
    }

    // If we can capture regardless of enemy response, it's an unstoppable 2-turn capture
    // Get the capture details from the first enemy response scenario
    const firstEnemyMove = enemyMoves[0];
    if (firstEnemyMove) {
      const stateAfterFirstEnemyMove = cloneFullGameState(stateAfterOurMove);
      const firstEnemyPiece = getPieceFromPosition(
        stateAfterFirstEnemyMove,
        firstEnemyMove.fromPosition
      );

      if (firstEnemyPiece && firstEnemyPiece.isLight !== isPlayerTurn) {
        try {
          applyMoveWithSnakeRespawn(
            stateAfterFirstEnemyMove,
            firstEnemyMove.fromPosition,
            firstEnemyMove.move,
            firstEnemyPiece
          );

          stateAfterFirstEnemyMove.isLightTurn =
            !stateAfterFirstEnemyMove.isLightTurn;

          const ourMovesAfterFirstEnemyResponse =
            MCTSMoveGenerationService.getInstance().getAllMoves(
              stateAfterFirstEnemyMove
            );

          for (const ourFinalMove of ourMovesAfterFirstEnemyResponse) {
            const finalMovePiece = MCTSUtils_v2.getPieceAt(
              stateAfterFirstEnemyMove.board,
              ourFinalMove.fromPosition
            );

            if (finalMovePiece && finalMovePiece.isLight === isPlayerTurn) {
              const targetPiece = MCTSUtils_v2.getPieceAt(
                stateAfterFirstEnemyMove.board,
                ourFinalMove.move.position
              );

              if (targetPiece && targetPiece.isLight !== isPlayerTurn) {
                const captureTarget = `${this.getPieceTypeString(
                  targetPiece.type
                )} at ${this.formatChessPosition(ourFinalMove.move.position)}`;
                const capturePlan = `${this.getPieceTypeString(
                  finalMovePiece.type
                )} from ${this.formatChessPosition(
                  ourFinalMove.fromPosition
                )} → ${this.formatChessPosition(ourFinalMove.move.position)}`;

                return {
                  canCaptureInTwoTurns: true,
                  targetDescription: captureTarget,
                  attackPlan: capturePlan,
                };
              }
            }
          }
        } catch (error) {
          // Continue to fallback
        }
      }
    }

    return {
      canCaptureInTwoTurns: true,
      targetDescription: "Enemy piece",
      attackPlan: "2-turn capture sequence",
    };
  }

  private findEnemyPieces(
    gameState: any,
    isPlayerTurn: boolean
  ): Array<{ piece: IPiece; position: Position }> {
    const enemyPieces: Array<{ piece: IPiece; position: Position }> = [];

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const piece = MCTSUtils_v2.getPieceAt(gameState.board, { row, col });
        if (piece && piece.isLight !== isPlayerTurn) {
          enemyPieces.push({
            piece,
            position: { row, col },
          });
        }
      }
    }

    return enemyPieces;
  }

  private addLog(message: string): void {
    this.analysisLogs.push(message);
  }

  private formatMove(move: MoveInfo): string {
    const piece = move.fromPosition
      ? MCTSUtils_v2.getPieceAt({} as any, move.fromPosition)
      : null;
    const pieceType = piece ? this.getPieceTypeString(piece.type) : "Piece";

    return `${pieceType} ${this.formatChessPosition(
      move.fromPosition
    )} → ${this.formatChessPosition(move.move.position)}`;
  }

  private formatChessPosition(position: Position): string {
    const files = ["A", "B", "C", "D", "E", "F", "G", "H"];
    return `${files[position.col]}${position.row + 1}`;
  }

  private getPieceTypeString(type: PieceType): string {
    switch (type) {
      case PieceType.Bear:
        return "Bear";
      case PieceType.Crane:
        return "Crane";
      case PieceType.Frog:
        return "Frog";
      case PieceType.Fungi:
        return "Fungi";
      case PieceType.Goat:
        return "Goat";
      case PieceType.Monkey:
        return "Monkey";
      case PieceType.Octopus:
        return "Octopus";
      case PieceType.Penguin:
        return "Penguin";
      case PieceType.Rhino:
        return "Rhino";
      case PieceType.Snake:
        return "Snake";
      case PieceType.Tree:
        return "Tree";
      case PieceType.Virus:
        return "Virus";
      case PieceType.Wolf:
        return "Wolf";
      default:
        return "Unknown";
    }
  }
}

